import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import EpisodeCard from "@/components/feature/EpisodeCard";
import EmptyState from "@/components/base/EmptyState";
import { useSeo } from "@/hooks/useSeo";
import { formatDate, getEpisodeById, getRelatedEpisodes } from "@/lib/content";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const episode = id ? getEpisodeById(id) : undefined;

  const [toast, setToast] = useState("");

  useSeo({
    title: episode
      ? `朋友的妈妈3剧情详情 - ${episode.title}`
      : "朋友的妈妈3剧情详情",
    description: episode
      ? `${episode.title}：${episode.summary}`
      : "朋友的妈妈3剧集详细剧情介绍。",
    keywords: episode
      ? `朋友的妈妈3,${episode.title},朋友的妈妈3剧情,朋友的妈妈3详情`
      : undefined,
  });

  const showToast = (text: string) => {
    setToast(text);
    setTimeout(() => setToast(""), 1800);
  };

  const scrollToSection = (sectionId: string) => {
    const el = document.getElementById(sectionId);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!episode) {
    return (
      <section className="w-full px-4 md:px-6 py-20 bg-background-50 min-h-[60vh] flex items-center justify-center">
        <div className="w-full max-w-xl">
          <EmptyState
            icon="ri-file-unknow-line"
            title="该内容已下架或不存在"
            description="你访问的剧情可能已被移除，或链接有误。可以返回分类页浏览其他章节。"
            actionLabel="返回剧情分类"
            onAction={() => navigate("/category")}
          />
        </div>
      </section>
    );
  }

  const related = getRelatedEpisodes(episode, 3);
  const shares = [
    { icon: "ri-wechat-line", label: "微信" },
    { icon: "ri-weibo-line", label: "微博" },
    { icon: "ri-link", label: "复制链接" },
  ];

  return (
    <>
      <section className="relative w-full">
        <div className="relative w-full h-[340px] md:h-[440px] overflow-hidden bg-foreground-950">
          <img
            src={episode.cover}
            alt={`${episode.title} 剧情封面`}
            title={`${episode.title} - ${episode.subtitle}`}
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/95 via-foreground-950/55 to-foreground-950/40"></div>
        </div>

        <div className="absolute inset-0 flex items-end">
          <div className="w-full px-4 md:px-6 pb-8 md:pb-12">
            <nav className="w-full text-sm text-background-100/60 mb-4" aria-label="面包屑">
              <ol className="flex items-center gap-2 flex-wrap">
                <li>
                  <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">
                    首页
                  </Link>
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li>
                  <Link to="/category" className="hover:text-accent-400 transition-colors cursor-pointer">
                    剧集分类
                  </Link>
                </li>
                <li><i className="ri-arrow-right-s-line"></i></li>
                <li className="text-background-50 truncate max-w-[60%]">{episode.title}</li>
              </ol>
            </nav>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent-500 text-primary-950 text-xs font-medium whitespace-nowrap">
              <i className="ri-film-line"></i>
              {episode.type}
            </span>
            <h1 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-background-50 leading-tight">
              {episode.title}
            </h1>
            <p className="mt-2 font-label text-base md:text-lg text-accent-400 tracking-wide">
              {episode.subtitle}
            </p>
          </div>
        </div>
      </section>

      <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <aside className="lg:col-span-3 lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-lg bg-background-100 border border-background-200/80 p-5 mb-6">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-4">
                剧集信息
              </h2>
              <dl className="space-y-3 text-sm">
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">剧集编号</dt>
                  <dd className="font-label text-foreground-900">EP.{String(episode.episodeNo).padStart(2, "0")}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">所属季</dt>
                  <dd className="text-foreground-900">{episode.season}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">上映日期</dt>
                  <dd className="text-foreground-900">{formatDate(episode.date)}</dd>
                </div>
                <div className="flex justify-between gap-3">
                  <dt className="text-foreground-500">时长</dt>
                  <dd className="text-foreground-900">{episode.duration}</dd>
                </div>
              </dl>
              <div className="mt-4 pt-4 border-t border-background-200/80">
                <p className="text-xs text-foreground-500 mb-2">主要演员</p>
                <ul className="space-y-2 text-sm">
                  {episode.actors.map((actor) => (
                    <li key={actor.role} className="flex items-center gap-2">
                      <span className="w-6 h-6 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-xs shrink-0">
                        <i className="ri-user-line"></i>
                      </span>
                      <span className="text-foreground-900">{actor.role}</span>
                      <span className="text-foreground-400 text-xs">饰</span>
                      <span className="text-foreground-500">{actor.actor}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="mt-4 pt-4 border-t border-background-200/80 flex flex-wrap gap-2">
                {episode.tags.map((tag) => (
                  <span key={tag} className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-lg bg-background-100 border border-background-200/80 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950 mb-3">
                目录导航
              </h2>
              <ul className="space-y-1">
                {episode.sections.map((section) => (
                  <li key={section.id}>
                    <button
                      type="button"
                      onClick={() => scrollToSection(section.id)}
                      className="w-full text-left px-3 py-2 rounded-md text-sm text-foreground-700 hover:bg-background-200 hover:text-primary-600 transition-colors cursor-pointer"
                    >
                      <i className="ri-hashtag text-foreground-300 mr-1"></i>
                      {section.title}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <article className="lg:col-span-9">
            <div className="rounded-lg bg-background-100 border border-background-200/80 p-5 md:p-8">
              {episode.sections.map((section) => (
                <div key={section.id} id={section.id} className="scroll-mt-24 mb-8 last:mb-0">
                  <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3 flex items-center gap-2">
                    <span className="w-1.5 h-5 rounded-full bg-accent-500"></span>
                    {section.title}
                  </h2>
                  <div className="space-y-3">
                    {section.paragraphs.map((p, i) => (
                      <p
                        key={i}
                        className="text-sm md:text-base text-foreground-700 leading-relaxed md:leading-loose"
                      >
                        {p}
                      </p>
                    ))}
                  </div>
                </div>
              ))}

              <div className="mt-8 pt-6 border-t border-background-200/80">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-sm text-foreground-500 flex items-center gap-1.5">
                    <i className="ri-share-line"></i>
                    分享到
                  </span>
                  {shares.map((item) => (
                    <button
                      key={item.label}
                      type="button"
                      onClick={() => showToast(`已触发「${item.label}」分享（前端演示）`)}
                      className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-700 hover:border-accent-300 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className={item.icon}></i>
                      {item.label}
                    </button>
                  ))}
                </div>
                <div className="mt-6 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md border border-background-300 text-foreground-800 hover:bg-background-200 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line"></i>
                    返回上一页
                  </button>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-home-line"></i>
                    回到首页
                  </Link>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>

      {related.length > 0 && (
        <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
          <div className="flex items-end justify-between gap-3 mb-6 md:mb-8">
            <div>
              <span className="inline-flex items-center gap-2 font-label text-xs tracking-[0.2em] uppercase text-accent-600 mb-2">
                <span className="w-6 h-px bg-accent-500"></span>
                Related
              </span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
                相关剧情推荐
              </h2>
            </div>
            <Link
              to="/category"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap shrink-0"
            >
              更多剧情
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {related.map((item) => (
              <EpisodeCard key={item.id} episode={item} />
            ))}
          </div>
        </section>
      )}

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-md bg-foreground-950 text-background-50 text-sm">
          {toast}
        </div>
      )}
    </>
  );
}